import { useCallback, useEffect, useRef, useState } from "react";
import type { ToastMessage, ToastTone } from "./contracts";
import { cx } from "./util";

const LIFETIME_MS = 4200;

export interface ToastStackProps {
  toasts: ToastMessage[];
  onDismiss: (id: number) => void;
}

export function ToastStack({ toasts, onDismiss }: ToastStackProps) {
  return (
    <div className="bd-toasts" aria-live="polite" aria-relevant="additions">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

export function Toast({
  toast,
  onDismiss,
}: {
  toast: ToastMessage;
  onDismiss: (id: number) => void;
}) {
  return (
    <div
      className={cx("slab chamfer-sm bd-toast bd-toast-in", `is-${toast.tone}`)}
      role={toast.tone === "error" ? "alert" : undefined}
    >
      <span className="grow t-body">{toast.text}</span>
      <button
        type="button"
        className="bd-toast-close"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss notification"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
  );
}

/**
 * Toast queue. Errors are the point of this component — SPEC wants clip
 * rejections "loud and specific" — so errors stay put until dismissed while
 * info and success expire on their own.
 */
export function useToasts() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, number>());

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const handle = timers.current.get(id);
    if (handle !== undefined) {
      window.clearTimeout(handle);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (text: string, tone: ToastTone = "info") => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, tone, text }]);
      if (tone !== "error") {
        timers.current.set(
          id,
          window.setTimeout(() => dismiss(id), LIFETIME_MS),
        );
      }
      return id;
    },
    [dismiss],
  );

  useEffect(() => {
    const handles = timers.current;
    return () => {
      handles.forEach((h) => window.clearTimeout(h));
      handles.clear();
    };
  }, []);

  return { toasts, push, dismiss };
}
